import { type ApplicationState } from '@my-medication/bff-interfaces-ui/shared/applicationState';

type AdminUserState = {
  id: string | undefined;
  customerId: string | undefined;
  firstname: string | undefined;
  lastname: string | undefined;
  username: string | undefined;
  email: string | undefined;
  newEmail: string | undefined;
  locale: string | undefined;
  fontSize: 'normal' | 'large' | 'x-large' | undefined;
  online: boolean | undefined;
  impersonatedUntil: Date | undefined;
  loggedInUntil: Date | undefined;
  authenticated: boolean;
  sessionReady: boolean;
  sessionInvalid: boolean;
  roles: string[];
};

const initialAdminUserState: AdminUserState = {
  id: undefined,
  customerId: undefined,
  firstname: undefined,
  lastname: undefined,
  username: undefined,
  email: undefined,
  newEmail: undefined,
  locale: undefined,
  fontSize: undefined,
  online: undefined,
  impersonatedUntil: undefined,
  loggedInUntil: undefined,
  authenticated: false,
  sessionReady: false,
  sessionInvalid: false,
  roles: [],
};

type AdminUserActions = {
  setUser: (payload: ApplicationState) => void;
  clearUser: () => void;
  setOnlineState: (online: boolean) => void;
  setLocale: (locale: string) => void;
  setFontSize: (fontSize: 'normal' | 'large' | 'x-large') => void;
  setImpersonated: (payload: { timeout: number } | null) => void;
  setLoggedInUntil: (payload: { timeout: number } | null) => void;
  setNewEmail: (newEmail: string) => void;
  setSessionReady: (ready: boolean) => void;
  setSessionInvalid: (invalid: boolean) => void;
  setRoles: (roles: string[]) => void;
};

const createAdminUserActions = (
  set: (fn: (store: { user: AdminUserState }) => Partial<{ user: AdminUserState }>) => void,
): AdminUserActions => ({
  setUser: (payload) =>
    set((store) => ({
      user: {
        ...store.user,
        id: payload.id,
        firstname: payload.firstname,
        lastname: payload.lastname,
        username: payload.username,
        email: payload.email,
        authenticated: payload.id != null,
      },
    })),

  clearUser: () =>
    set((store) => ({
      user: {
        ...store.user,
        id: undefined,
        customerId: undefined,
        username: undefined,
        authenticated: false,
        sessionInvalid: false,
      },
    })),

  setOnlineState: (online) => set((s) => ({ user: { ...s.user, online } })),

  setLocale: (locale) => set((s) => ({ user: { ...s.user, locale } })),

  setFontSize: (fontSize) => set((s) => ({ user: { ...s.user, fontSize } })),

  setImpersonated: (payload) =>
    set((s) => ({
      user: {
        ...s.user,
        impersonatedUntil: payload != null ? new Date(new Date().getTime() + payload.timeout * 1000) : undefined,
      },
    })),

  setLoggedInUntil: (payload) =>
    set((s) => ({
      user: {
        ...s.user,
        loggedInUntil: payload != null ? new Date(new Date().getTime() + payload.timeout * 1000) : undefined,
      },
    })),

  setNewEmail: (newEmail) => set((s) => ({ user: { ...s.user, newEmail } })),

  setSessionReady: (sessionReady) => set((s) => ({ user: { ...s.user, sessionReady } })),

  setSessionInvalid: (sessionInvalid) =>
    set((s) => (s.user.sessionInvalid === sessionInvalid ? s : { user: { ...s.user, sessionInvalid } })),

  setRoles: (roles) => set((s) => ({ user: { ...s.user, roles } })),
});

export type { AdminUserState, AdminUserActions };
export { initialAdminUserState, createAdminUserActions };
